import React from 'react';
import Icon from '../../../components/AppIcon';
import {Button} from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/localization';

const BalanceSummary = ({ balances, onSettleUp }) => {
  const getBalanceColor = (amount) => {
    if (amount > 0) return 'text-success';
    if (amount < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  const getBalanceIcon = (amount) => {
    if (amount > 0) return 'TrendingUp';
    if (amount < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getBalanceText = (amount) => {
    if (amount > 0) return `Gets back ${formatCurrency(amount)}`;
    if (amount < 0) return `Owes ${formatCurrency(Math.abs(amount))}`;
    return 'Settled up';
  };

  const hasUnsettledBalances = balances?.some(balance => Math.abs(balance?.amount) > 0.01);

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Balance Summary</h2>
          {hasUnsettledBalances && (
            <Button
              variant="outline"
              size="sm"
              iconName="DollarSign"
              iconPosition="left"
              onClick={onSettleUp}
            >
              Settle Up
            </Button>
          )}
        </div>
      </div>
      <div className="p-6">
        {balances?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No participants added yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {balances?.map((balance) => (
              <div
                key={balance?.participant?.id}
                className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-foreground">
                      {balance?.participant?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{balance?.participant?.name}</p>
                    <p className="text-sm text-muted-foreground">{balance?.participant?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`font-semibold ${getBalanceColor(balance?.amount)}`}>
                      {getBalanceText(balance?.amount)}
                    </p>
                    <div className="flex items-center gap-1 justify-end">
                      <Icon
                        name={getBalanceIcon(balance?.amount)}
                        size={14}
                        className={getBalanceColor(balance?.amount)}
                      />
                      <span className="text-xs text-muted-foreground">
                        {Math.abs(balance?.amount) > 0.01 ? 'Unsettled' : 'Settled'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Settlement Suggestions */}
            {hasUnsettledBalances && (
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="text-sm font-medium text-foreground mb-3">Suggested Settlements</h3>
                <div className="space-y-2">
                  {balances?.filter(b => b?.amount < -0.01)?.slice(0, 3)?.map((debtor) => {
                      const creditor = balances?.find(b => b?.amount > 0.01);
                      if (!creditor) return null;
                      
                      const settlementAmount = Math.min(Math.abs(debtor?.amount), creditor?.amount);
                      
                      return (
                        <div
                          key={`${debtor?.participant?.id}-${creditor?.participant?.id}`}
                          className="flex items-center justify-between p-3 bg-accent/10 rounded-md"
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-foreground">
                              {debtor?.participant?.name}
                            </span>
                            <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                            <span className="font-medium text-foreground">
                              {creditor?.participant?.name}
                            </span>
                          </div>
                          <span className="font-semibold text-accent">
                            {formatCurrency(settlementAmount)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceSummary;
